// Pure search core for the local-first BSB Bible (M.3.6.3.c).
//
// Probe-importable leaf module: its only runtime imports are the
// zero-import leaves `./search-normalize` and `./edit-distance` plus
// `./bible-search/word-relations` (08.5; itself imports only
// normalizeForSearch); the `./bsb-types` imports are type-only and erased
// at runtime. Relative paths only. No IDB, no React, no @/-aliases.
//
// `searchVersesIn` is the substring + AND-semantic match algorithm.
// The IDB-fetching wrapper lives in `bsb-search.ts` and is
// browser-only. Splitting the pure core out keeps the algorithm
// probe-testable against an inline fixture without an IDB shim.
//
// Text normalization (NFD + diacritic stripping + whitespace collapse)
// comes from the canonical `normalizeForSearch` (a zero-import leaf, so
// this module stays probe-importable). The parser uses simpler
// normalization because Bible book names don't carry accents.
//
// Plan 02 upgraded the boolean substring matcher to a relevance-ranked
// engine with a typo-tolerant fuzzy fallback. The AND-substring pass is
// the admission filter (rule #38: a verse must contain ALL query words);
// survivors are SCORED (contiguous phrase > tight cluster > dense/short
// verse > canonical order) instead of returned in input order. The fuzzy
// pass (osaDistance, mirroring rankPrayersFuzzy) runs ONLY when the exact
// pass yields zero hits, so a good exact query is never degraded. Both
// passes share the `SearchResult` return contract (chunk-03 dependency):
// each hit carries `spans` = character offsets INTO its own `snippet`, so
// the UI highlights by slicing, never re-searches at render time.

import { normalizeForSearch } from './search-normalize';
import { osaDistance, commonPrefixLength } from './edit-distance';
import { relationsFor } from './bible-search/word-relations';
import type { BsbBlock, BsbBook, BsbVerse, TextSegment } from './bsb-types';

export interface SearchOptions {
  /** Maximum number of hits returned. Default 50. */
  limit?: number;
  /** Reserved for future locale-specific normalization rules. */
  locale?: string;
}

/**
 * Half-open [start, end) CHARACTER offsets into the owning hit's `snippet`
 * string (the rendered text, not the raw verse, not the normalized text),
 * sorted ascending and non-overlapping. The UI highlights by slicing
 * `snippet` on these spans, so no re-search at render time.
 */
export interface VerseMatchSpan {
  start: number;
  end: number;
}

export interface SearchHit {
  bookSlug: string;
  bookName: string;
  chapter: number;
  verse: number;
  /** Verse text as stored in IDB (unmodified). */
  text: string;
  /** ~120-char plain-text window centered on the ranked match cluster. */
  snippet: string;
  /** Matched word spans within `snippet` (exact words, or fuzzy near-miss tokens). */
  spans: VerseMatchSpan[];
  /**
   * The unique, normalized needle tokens that drove this match (exact pass: the
   * query words; fuzzy pass: the distinct near-miss verse tokens). Unlike
   * `spans` (offsets into `snippet`, for slice-highlighting the result list),
   * this is a whole-verse token list. DORMANT (arc 07.4, R4): it formerly fed a
   * per-word gold glow in the chapter reader, but the reader now golds the whole
   * landed `?v=` verse directly (no per-word matching, no cross-page hand-off),
   * so nothing consumes this field today. Retained (still produced + tested) as a
   * normalized matched-token list for any future consumer. Always de-duped and
   * already normalizeForSearch-folded.
   */
  matchedTokens: string[];
}

export interface SearchResult {
  hits: SearchHit[];
  /**
   * TRUE iff the exact pass was empty and these are the fuzzy fallback
   * (drives chunk 03's "close matches" note). All hits in one result share
   * this; a result is never a mix of exact and fuzzy hits.
   */
  fuzzy: boolean;
  /** hits.length reached `limit` with more survivors available (drives the "refine" hint). */
  capped: boolean;
  /**
   * Strength of the leading hit (08.4), for the Top-preview Verses↔Topics
   * ordering. Computed at the result boundary so a single word maps to 'word',
   * NOT 'phrase' (the internal exact-pass `phrase` flag is true for every
   * single-word query, which would make the ordering rule a no-op):
   *  'phrase'    = MULTI-WORD query matched contiguously (strong "this verse")
   *  'all-words' = multi-word, all present but scattered (weaker)
   *  'word'      = single-word literal hit (a lone word is weak evidence)
   *  'related'   = surfaced via the curated related-wording pass (08.5)
   *  'fuzzy'     = surfaced via the fuzzy fallback
   *  null        = no hits
   */
  topMatchKind: 'phrase' | 'all-words' | 'word' | 'related' | 'fuzzy' | null;
  /**
   * Related-wording substitutions actually present in the returned hit set
   * (08.5), for the honest `relatedNote` label. Ordered by first appearance of
   * `from` in the query (deterministic). Empty on every non-related result.
   */
  relatedTerms: Array<{ from: string; to: string }>;
  /**
   * Metrics denominator (08.5): every (query-word, its-relation) pair AVAILABLE
   * for this query on a zero-exact pass, whether or not it produced hits.
   * Distinct from `relatedTerms` (which carries only substitutions in the
   * returned hits). Absent when the related pass did not run (exact hit, or no
   * query word had a relation). DEV-only UI logging reads this; it never leaves
   * the device (rule 07.4).
   */
  relatedAttemptedTerms?: Array<{ from: string; to: string }>;
}

const SNIPPET_LEN = 120;
const ELLIPSIS = '...';

// Builds a ~120-char window centered on the RANKED-MATCH anchor region
// [anchorStart, anchorEnd) (the contiguous phrase, or the tightest cluster
// covering all words, that caused this verse to rank). Anchoring on the
// ranked match, not the first occurrence of the first query word, is what
// keeps the snippet showing the words that actually matched even when the
// match is late in a long verse. Anchor offsets are in normalized-text
// space; for NFC-form English / common Latin-1 text, NFD + diacritic
// stripping preserves length, so those offsets map to the same index in the
// original text. Multi-codepoint diacritics may be off by 1-2 chars, which
// is acceptable for visual snippet display.
function buildSnippet(
  text: string,
  anchorStart: number,
  anchorEnd: number,
): string {
  if (anchorStart < 0) return text.slice(0, SNIPPET_LEN);

  const halfWindow = Math.floor(SNIPPET_LEN / 2);
  let start = Math.max(0, anchorStart - halfWindow);
  let end = Math.min(text.length, anchorEnd + halfWindow);

  // Snap to word boundaries so we don't slice mid-word, but never cut into
  // the anchor region itself.
  if (start > 0) {
    const nextSpace = text.indexOf(' ', start);
    if (nextSpace > -1 && nextSpace < anchorStart) start = nextSpace + 1;
  }
  if (end < text.length) {
    const prevSpace = text.lastIndexOf(' ', end);
    if (prevSpace > -1 && prevSpace > anchorEnd) end = prevSpace;
  }

  let snippet = text.slice(start, end).trim();
  if (start > 0) snippet = ELLIPSIS + snippet;
  if (end < text.length) snippet = snippet + ELLIPSIS;
  return snippet;
}

interface SearchableVerse {
  v: number;
  text: string;
}

function segmentsToText(t: string | TextSegment[]): string {
  if (typeof t === 'string') return t;
  return t.map((seg) => seg.text).join('');
}

// Aggregates vCont fragments by verse number and produces a flat
// {v, text}[] for a chapter. Document order from the block walk is
// preserved (Mark 9:43-46 emits verses in non-ascending source order;
// search results reflect that order rather than imposing numeric
// reordering). Omitted verses are excluded from the result set.
function extractVersesForSearch(blocks: BsbBlock[]): SearchableVerse[] {
  const byVerse = new Map<number, { text: string; omitted: boolean }>();

  const collectVerse = (verse: BsbVerse): void => {
    if (verse.omitted === true) {
      if (!byVerse.has(verse.v)) {
        byVerse.set(verse.v, { text: '', omitted: true });
      }
      return;
    }
    const text = segmentsToText(verse.t);
    const existing = byVerse.get(verse.v);
    if (existing) {
      existing.text = existing.text + ' ' + text;
      existing.omitted = false;
    } else {
      byVerse.set(verse.v, { text, omitted: false });
    }
  };

  for (const block of blocks) {
    if (block.type === 'paragraph') {
      for (const verse of block.verses) collectVerse(verse);
    } else if (block.type === 'poetry') {
      for (const line of block.lines) {
        for (const verse of line.verses) collectVerse(verse);
      }
    }
    // Skip acrostic, title, section, majorSection, reference, blank.
  }

  const result: SearchableVerse[] = [];
  for (const [v, entry] of byVerse) {
    if (entry.omitted) continue;
    result.push({ v, text: entry.text });
  }
  return result;
}

// --- Span computation (slice-to-highlight, the chunk-03 dependency) -------
//
// Spans index into the FINAL `snippet` string. We re-fold the snippet itself
// (NFD + strip combining marks + lowercase, WITHOUT whitespace collapse or
// trim) so offsets stay 1:1 with the snippet for the common NFC/ASCII case
// (BSB English). Each needle is already normalized (lowercase, accent-folded),
// so indexOf against the folded snippet is apples-to-apples. The leading /
// trailing "..." ellipses carry no alphanumerics and never match a needle.
const COMBINING_MARKS = /[\u0300-\u036f]/g;

function foldForSpans(s: string): string {
  return s.normalize('NFD').replace(COMBINING_MARKS, '').toLowerCase();
}

function computeSpans(snippet: string, needles: string[]): VerseMatchSpan[] {
  const folded = foldForSpans(snippet);
  const raw: VerseMatchSpan[] = [];
  for (const needle of needles) {
    if (!needle) continue;
    const i = folded.indexOf(needle);
    if (i >= 0) raw.push({ start: i, end: i + needle.length });
  }
  raw.sort((a, b) => (a.start !== b.start ? a.start - b.start : a.end - b.end));

  // Merge overlapping / touching spans so the result is sorted AND
  // non-overlapping (the chunk-03 slice-to-highlight contract).
  const merged: VerseMatchSpan[] = [];
  for (const span of raw) {
    const last = merged[merged.length - 1];
    if (last && span.start <= last.end) {
      if (span.end > last.end) last.end = span.end;
    } else {
      merged.push({ start: span.start, end: span.end });
    }
  }
  return merged;
}

function makeHit(
  book: BsbBook,
  chapterNum: number,
  verse: SearchableVerse,
  anchorStart: number,
  anchorEnd: number,
  spanNeedles: string[],
): SearchHit {
  const snippet = buildSnippet(verse.text, anchorStart, anchorEnd);
  return {
    bookSlug: book.slug,
    bookName: book.book,
    chapter: chapterNum,
    verse: verse.v,
    text: verse.text,
    snippet,
    spans: computeSpans(snippet, spanNeedles),
    // De-dupe the needles (the exact pass passes raw query `words`, which may
    // repeat); they are already normalized. This is the reader-glow source.
    matchedTokens: [...new Set(spanNeedles.filter(Boolean))],
  };
}

// Shared book/chapter/verse walk. `index` is the running canonical position,
// used as the stable final tie-break so equal-scored hits keep canonical
// order. Books arrive pre-sorted by canonical order from getAllBooksFromIdb.
function forEachVerse(
  books: BsbBook[],
  fn: (
    book: BsbBook,
    chapterNum: number,
    verse: SearchableVerse,
    normalizedText: string,
    index: number,
  ) => void,
): void {
  let index = 0;
  for (const book of books) {
    const chapterKeys = Object.keys(book.chapters).sort(
      (a, b) => Number(a) - Number(b),
    );
    for (const chapterKey of chapterKeys) {
      const chapterNum = Number(chapterKey);
      const verses = extractVersesForSearch(book.chapters[chapterKey]);
      for (const verse of verses) {
        fn(book, chapterNum, verse, normalizeForSearch(verse.text), index);
        index += 1;
      }
    }
  }
}

// Exact-match ranking is a STRICT tuple order, not an additive score, so each
// key dominates the next outright (an additive score with equal caps would let
// a shorter scattered verse outrank a longer tight-cluster verse). Locked order
// (per the plan): contiguous phrase first, then the tightest word cluster
// (proximity), then the shorter/denser verse, then canonical position as the
// stable tie-break. Mirrors the spirit of rankPrayers without importing it.

// All start offsets of `word` in `text`, allowing overlapping occurrences so
// the cluster search considers every placement, not just the first.
function allOccurrences(text: string, word: string): number[] {
  const starts: number[] = [];
  let from = 0;
  for (;;) {
    const i = text.indexOf(word, from);
    if (i < 0) break;
    starts.push(i);
    from = i + 1;
  }
  return starts;
}

// Smallest character window that contains at least one occurrence of EVERY
// distinct term, considering all occurrences. Returns the window's `start` (so
// the snippet can be anchored on the ranked match) and its `width` (the
// proximity ranking key). Classic minimal-covering-window: merge all
// occurrences sorted by start, slide a window that keeps every term covered,
// track the minimal (max end in window) - (window-left start). Every term is
// guaranteed present (the caller only passes terms it confirmed are in the
// text), so the window is always found. Occurrence counts per verse are tiny,
// so the inner max-end recompute is cheap.
interface ClusterWindow {
  start: number;
  width: number;
}

function bestClusterWindow(normalizedText: string, terms: string[]): ClusterWindow {
  interface Occ { start: number; end: number; term: number; }
  const occ: Occ[] = [];
  for (let w = 0; w < terms.length; w += 1) {
    const len = terms[w].length;
    for (const start of allOccurrences(normalizedText, terms[w])) {
      occ.push({ start, end: start + len, term: w });
    }
  }
  occ.sort((a, b) => a.start - b.start);

  const have = new Map<number, number>();
  let covered = 0;
  let bestWidth = Infinity;
  let bestStart = occ.length > 0 ? occ[0].start : 0;
  let left = 0;
  for (let right = 0; right < occ.length; right += 1) {
    const wr = occ[right].term;
    const prev = have.get(wr) ?? 0;
    have.set(wr, prev + 1);
    if (prev === 0) covered += 1;

    while (covered === terms.length) {
      let maxEnd = 0;
      for (let k = left; k <= right; k += 1) {
        if (occ[k].end > maxEnd) maxEnd = occ[k].end;
      }
      const width = maxEnd - occ[left].start;
      if (width < bestWidth) {
        bestWidth = width;
        bestStart = occ[left].start;
      }

      const wl = occ[left].term;
      const c = have.get(wl) ?? 0;
      have.set(wl, c - 1);
      if (c - 1 === 0) covered -= 1;
      left += 1;
    }
  }
  return { start: bestStart, width: bestWidth };
}

interface ScoredExact {
  book: BsbBook;
  chapterNum: number;
  verse: SearchableVerse;
  textLength: number;
  index: number;
  phrase: boolean; // contiguous full-query occurrence (always true for 1 word)
  cluster: ClusterWindow; // tightest cluster covering all distinct words
}

function runExactPass(
  books: BsbBook[],
  words: string[],
  normalizedQuery: string,
  limit: number,
): SearchResult {
  // Distinct words for the cluster math so a repeated query word (e.g.
  // "lord lord") doesn't make the covering window unsatisfiable.
  const uniqueWords = [...new Set(words)];
  const scored: ScoredExact[] = [];
  forEachVerse(books, (book, chapterNum, verse, normalizedText, index) => {
    for (const w of words) {
      if (!normalizedText.includes(w)) return; // strict AND admission filter
    }
    scored.push({
      book,
      chapterNum,
      verse,
      textLength: normalizedText.length,
      index,
      // Single-word queries: the word IS the phrase (uniform across survivors).
      phrase: words.length === 1 || normalizedText.includes(normalizedQuery),
      cluster: bestClusterWindow(normalizedText, uniqueWords),
    });
  });

  // Strict tuple order: phrase > tighter cluster > shorter verse > canonical.
  scored.sort((a, b) => {
    if (a.phrase !== b.phrase) return a.phrase ? -1 : 1;
    if (a.cluster.width !== b.cluster.width) return a.cluster.width - b.cluster.width;
    if (a.textLength !== b.textLength) return a.textLength - b.textLength;
    return a.index - b.index;
  });

  const capped = scored.length > limit;
  const hits = scored
    .slice(0, limit)
    .map((s) =>
      // Anchor the snippet on the ranked cluster (not words[0]'s first
      // occurrence), so a late tight cluster still shows all its words.
      makeHit(
        s.book,
        s.chapterNum,
        s.verse,
        s.cluster.start,
        s.cluster.start + s.cluster.width,
        words,
      ),
    );
  // topMatchKind (08.4): guard scored-empty FIRST (a zero-exact query, incl. a
  // multi-word one, must never read scored[0]); a single word is 'word', not
  // 'phrase'; else scored[0] decides (hits sorted phrase-first).
  const topMatchKind: SearchResult['topMatchKind'] =
    scored.length === 0
      ? null
      : words.length === 1
        ? 'word'
        : scored[0].phrase
          ? 'phrase'
          : 'all-words';
  return { hits, fuzzy: false, capped, topMatchKind, relatedTerms: [] };
}

// --- Fuzzy fallback (zero-exact-results only) -----------------------------
//
// Mirrors rankPrayersFuzzy: words shorter than FUZZY_MIN_LEN are never fuzzed
// (short words are too collision-prone: son/sun/sin), the OSA threshold is 1
// for [4, 7) and 2 for >= 7, and a MULTI-word query needs at least one exact
// token anchor so a broad misspelling can't all-fuzzy its way to confident
// junk. A single-word query may be wholly fuzzy (the one word is the query).
const FUZZY_MIN_LEN = 4;
const FUZZY_LONG_LEN = 7;
const TOKEN_SPLIT_RE = /[^a-z0-9]+/;

function fuzzyThreshold(len: number): number {
  return len >= FUZZY_LONG_LEN ? 2 : 1;
}

interface FuzzyVerseMatch {
  totalDist: number;
  // Match-quality signals summed over the best token per query word, used to
  // rank verses that tie on totalDist. A misspelling can be edit-equidistant
  // from the intended word AND several decoys (e.g. "shepard" is distance 2
  // from "shepherd", "sheared", "steward", and "heard"). The strongest signal
  // is the shared prefix: most typos preserve the start, so the intended word
  // shares the longest leading run ("shep" = 4 for shepherd, vs 3/1/0 for the
  // decoys). Prefix is ranked BEFORE length delta, because same-length decoys
  // ("sheared"/"steward" have length delta 0 vs shepherd's 1) would otherwise
  // win on delta despite a weaker prefix. totalPrefix is higher-is-better.
  totalPrefix: number;
  totalLenDelta: number;
  tokensByWord: string[]; // the matched verse token per query word (exact or near-miss)
}

function evalFuzzyVerse(
  words: string[],
  normalizedText: string,
  multiWord: boolean,
): FuzzyVerseMatch | null {
  const tokens = normalizedText.split(TOKEN_SPLIT_RE).filter(Boolean);
  const tokenSet = new Set(tokens);
  let totalDist = 0;
  let totalLenDelta = 0;
  let totalPrefix = 0;
  let anyExact = false;
  const tokensByWord: string[] = [];

  for (const w of words) {
    if (tokenSet.has(w)) {
      anyExact = true;
      tokensByWord.push(w);
      totalPrefix += w.length; // exact: zero dist + delta, perfect prefix
      continue;
    }
    const len = w.length;
    if (len < FUZZY_MIN_LEN) return null;
    const maxDist = fuzzyThreshold(len);
    let bestDist = Infinity;
    let bestLenDelta = Infinity;
    let bestPrefix = -1;
    let bestToken = '';
    for (const t of tokenSet) {
      // OSA distance is at least the length difference; skip tokens that
      // cannot possibly be in threshold before the O(len*tokenLen) DP.
      if (Math.abs(t.length - len) > maxDist) continue;
      const d = osaDistance(w, t);
      if (d > maxDist) continue;
      const lenDelta = Math.abs(t.length - len);
      const prefix = commonPrefixLength(w, t);
      // Pick the best-quality token for this word: lower distance, then
      // longer shared prefix, then smaller length delta. Prefix before delta
      // is what lets a misspelling prefer its intended word ("shepherd",
      // prefix 4) over same-distance same-length decoys ("sheared" prefix 3,
      // "steward" prefix 1) and shorter common words ("heard" prefix 0).
      if (
        d < bestDist ||
        (d === bestDist && prefix > bestPrefix) ||
        (d === bestDist && prefix === bestPrefix && lenDelta < bestLenDelta)
      ) {
        bestDist = d;
        bestPrefix = prefix;
        bestLenDelta = lenDelta;
        bestToken = t;
      }
    }
    if (bestDist === Infinity) return null;
    totalDist += bestDist;
    totalLenDelta += bestLenDelta;
    totalPrefix += bestPrefix;
    tokensByWord.push(bestToken);
  }

  if (multiWord && !anyExact) return null;
  return { totalDist, totalLenDelta, totalPrefix, tokensByWord };
}

interface ScoredFuzzy {
  book: BsbBook;
  chapterNum: number;
  verse: SearchableVerse;
  normalizedText: string;
  index: number;
  dist: number;
  lenDelta: number;
  prefix: number;
  tokens: string[];
}

function runFuzzyPass(
  books: BsbBook[],
  words: string[],
  multiWord: boolean,
  limit: number,
): SearchResult {
  const scored: ScoredFuzzy[] = [];
  forEachVerse(books, (book, chapterNum, verse, normalizedText, index) => {
    const match = evalFuzzyVerse(words, normalizedText, multiWord);
    if (!match) return;
    scored.push({
      book,
      chapterNum,
      verse,
      normalizedText,
      index,
      dist: match.totalDist,
      lenDelta: match.totalLenDelta,
      prefix: match.totalPrefix,
      tokens: match.tokensByWord,
    });
  });

  // Lowest total edit distance first, THEN match quality (stronger shared
  // prefix, then smaller length delta) so a misspelling surfaces its intended
  // word over same-distance decoys (prefix beats delta: same-length decoys
  // like "sheared"/"steward" would win on delta otherwise), THEN the existing
  // domain ranking (shorter/denser verse, then canonical order as the stable
  // tie-break).
  scored.sort((a, b) =>
    a.dist !== b.dist
      ? a.dist - b.dist
      : a.prefix !== b.prefix
        ? b.prefix - a.prefix
        : a.lenDelta !== b.lenDelta
          ? a.lenDelta - b.lenDelta
          : a.normalizedText.length !== b.normalizedText.length
            ? a.normalizedText.length - b.normalizedText.length
            : a.index - b.index,
  );

  const capped = scored.length > limit;
  const hits = scored.slice(0, limit).map((s) => {
    // Distinct matched tokens so highlighting lands on the near-miss words.
    const distinctTokens = [...new Set(s.tokens)];
    // Anchor on the tightest cluster of matched tokens (same fix as the exact
    // pass) so the snippet shows every near-miss token even when they sit late
    // in a long verse.
    const cluster = bestClusterWindow(s.normalizedText, distinctTokens);
    return makeHit(
      s.book,
      s.chapterNum,
      s.verse,
      cluster.start,
      cluster.start + cluster.width,
      distinctTokens,
    );
  });
  // `fuzzy` is true only when the fallback actually surfaced something; a
  // fuzzy pass that finds nothing collapses to the empty/exact-false result
  // (no "close matches" note over an empty list).
  return {
    hits,
    fuzzy: hits.length > 0,
    capped: hits.length > 0 && capped,
    topMatchKind: hits.length > 0 ? 'fuzzy' : null,
    relatedTerms: [],
  };
}

// --- Related-wording fallback (zero-exact-results only) -------------------
//
// Curated word-equivalence substitution (08.5), higher-confidence than fuzzy
// (a curated synonym, not a typo guess), so it runs BEFORE fuzzy. Admission is
// the generalized AND: a verse qualifies iff for EVERY query word it contains
// the word ITSELF or one of relationsFor(word) (per-word OR, across-words AND).
// A multi-word query needs >=1 word matched by its ORIGINAL form (mirrors the
// fuzzy anchor) so a broad substitution can't manufacture junk. Ranking reuses
// the EXACT-pass cluster style (tightest cluster of the matched needles, then
// shorter verse, then canonical) — NOT fuzzy edit-distance — because the
// needles are real words: that is what makes the three contiguous "faith
// without deeds" verses resolve to the shortest (James 2:26).
interface ScoredRelated {
  book: BsbBook;
  chapterNum: number;
  verse: SearchableVerse;
  textLength: number;
  index: number;
  cluster: ClusterWindow;
  needles: string[]; // distinct matched needles (original or relation), for makeHit
  subs: Array<{ from: string; to: string }>; // substitutions used in THIS verse
}

function evalRelatedVerse(
  words: string[],
  normalizedText: string,
  multiWord: boolean,
): { needles: string[]; subs: Array<{ from: string; to: string }> } | null {
  const tokens = normalizedText.split(TOKEN_SPLIT_RE).filter(Boolean);
  const tokenSet = new Set(tokens);
  const needles: string[] = [];
  const subs: Array<{ from: string; to: string }> = [];
  let anyOriginal = false;
  for (const w of words) {
    if (tokenSet.has(w)) {
      anyOriginal = true;
      needles.push(w);
      continue;
    }
    let hit = '';
    for (const rel of relationsFor(w)) {
      if (tokenSet.has(rel)) {
        hit = rel;
        break;
      }
    }
    if (!hit) return null; // this word matched neither itself nor any relation
    needles.push(hit);
    subs.push({ from: w, to: hit });
  }
  // Multi-word anchor: at least one word matched by its ORIGINAL form. (A verse
  // with ALL originals can't reach here — exact ran first and returned zero.)
  if (multiWord && !anyOriginal) return null;
  return { needles, subs };
}

function runRelatedPass(
  books: BsbBook[],
  words: string[],
  multiWord: boolean,
  limit: number,
): SearchResult {
  const scored: ScoredRelated[] = [];
  forEachVerse(books, (book, chapterNum, verse, normalizedText, index) => {
    const match = evalRelatedVerse(words, normalizedText, multiWord);
    if (!match) return;
    const distinct = [...new Set(match.needles)];
    scored.push({
      book,
      chapterNum,
      verse,
      textLength: normalizedText.length,
      index,
      cluster: bestClusterWindow(normalizedText, distinct),
      needles: distinct,
      subs: match.subs,
    });
  });

  // Tighter cluster > shorter verse > canonical (the exact-pass domain ranking,
  // minus the phrase flag — the needles are post-substitution, so contiguity is
  // captured by cluster width).
  scored.sort((a, b) =>
    a.cluster.width !== b.cluster.width
      ? a.cluster.width - b.cluster.width
      : a.textLength !== b.textLength
        ? a.textLength - b.textLength
        : a.index - b.index,
  );

  const capped = scored.length > limit;
  const top = scored.slice(0, limit);
  const hits = top.map((s) =>
    makeHit(
      s.book,
      s.chapterNum,
      s.verse,
      s.cluster.start,
      s.cluster.start + s.cluster.width,
      s.needles,
    ),
  );

  // relatedTerms: distinct substitutions present in the RETURNED hits, ordered
  // by first appearance of `from` in the query (deterministic label).
  const firstTo = new Map<string, string>();
  for (const s of top) {
    for (const sub of s.subs) {
      if (!firstTo.has(sub.from)) firstTo.set(sub.from, sub.to);
    }
  }
  const seen = new Set<string>();
  const relatedTerms: Array<{ from: string; to: string }> = [];
  for (const w of words) {
    const to = firstTo.get(w);
    if (to !== undefined && !seen.has(w)) {
      seen.add(w);
      relatedTerms.push({ from: w, to });
    }
  }

  return {
    hits,
    fuzzy: false,
    capped: hits.length > 0 && capped,
    topMatchKind: hits.length > 0 ? 'related' : null,
    relatedTerms,
  };
}

export function searchVersesIn(
  books: BsbBook[],
  query: string,
  options: SearchOptions = {},
): SearchResult {
  // Default 50 if not provided; clamp non-positive limits back to the
  // default so a zero or negative input doesn't surprise the caller.
  const rawLimit = options.limit ?? 50;
  const limit = rawLimit > 0 ? rawLimit : 50;
  const normalizedQuery = normalizeForSearch(query);
  if (normalizedQuery.length === 0)
    return { hits: [], fuzzy: false, capped: false, topMatchKind: null, relatedTerms: [] };

  const words = normalizedQuery.split(' ').filter((w) => w.length > 0);
  if (words.length === 0)
    return { hits: [], fuzzy: false, capped: false, topMatchKind: null, relatedTerms: [] };

  const exact = runExactPass(books, words, normalizedQuery, limit);
  if (exact.hits.length > 0) return exact;

  // Zero exact hits (rule #38: the only sanctioned relaxations, exact set empty).
  // Try the curated related-wording pass BEFORE fuzzy (a curated synonym beats a
  // typo guess). `attempted` = every (word, relation) pair available for this
  // query — the metrics DENOMINATOR (relatedAttemptedTerms), deduped; it also
  // gates whether the related pass runs at all.
  const multiWord = words.length > 1;
  const attempted: Array<{ from: string; to: string }> = [];
  const seenPair = new Set<string>();
  for (const w of words) {
    for (const to of relationsFor(w)) {
      const key = `${w}\0${to}`;
      if (!seenPair.has(key)) {
        seenPair.add(key);
        attempted.push({ from: w, to });
      }
    }
  }
  if (attempted.length > 0) {
    const related = runRelatedPass(books, words, multiWord, limit);
    if (related.hits.length > 0) return { ...related, relatedAttemptedTerms: attempted };
  }

  const fuzzy = runFuzzyPass(books, words, multiWord, limit);
  return attempted.length > 0 ? { ...fuzzy, relatedAttemptedTerms: attempted } : fuzzy;
}
